/**
 * Mirrors backend/utils/invoice-service-description.js for on-screen invoice view.
 */

export function formatServiceEntityLabel(ent) {
  if (!ent || typeof ent !== "object") return "";
  const d = ent.details;
  if (d && typeof d === "object") {
    if (d.name) return String(d.name).trim();
    if (d.description) return String(d.description).trim();
    if (d.title) return String(d.title).trim();
  }
  return (
    (ent.name && String(ent.name).trim()) ||
    (ent.description && String(ent.description).trim()) ||
    (ent.reference_id != null && String(ent.reference_id).trim()) ||
    (ent.entity_type && String(ent.entity_type).trim()) ||
    (ent.id != null && String(ent.id).trim()) ||
    ""
  );
}

export function buildInvoiceServiceDescription(service) {
  const serviceName = service.name || service.description || "Service";
  const longDescription =
    service.description && String(service.description).trim();
  const prettyLabel = (key) =>
    key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  let extraRoot = service.extra_fields;
  if (extraRoot == null) extraRoot = {};
  if (typeof extraRoot === "string") {
    try {
      extraRoot = extraRoot.trim() ? JSON.parse(extraRoot) : {};
    } catch {
      extraRoot = {};
    }
  }
  if (typeof extraRoot !== "object" || Array.isArray(extraRoot)) {
    extraRoot = {};
  }

  const normalizeValue = (value) => {
    if (value == null) return "";
    if (Array.isArray(value)) {
      return value
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .join(", ");
    }
    if (typeof value === "object") {
      return Object.values(value)
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .join(", ");
    }
    const raw = String(value).trim();
    if (!raw) return "";
    if (raw.startsWith("[") || raw.startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed
            .map((v) => String(v || "").trim())
            .filter(Boolean)
            .join(", ");
        }
        if (parsed && typeof parsed === "object") {
          return Object.values(parsed)
            .map((v) => String(v || "").trim())
            .filter(Boolean)
            .join(", ");
        }
      } catch (_) {
        /* keep raw */
      }
    }
    return raw;
  };

  const partsFromObject = (extra) => {
    if (!extra || typeof extra !== "object") return [];
    return Object.entries(extra)
      .map(([key, value]) => {
        const normalizedValue = normalizeValue(value);
        if (!normalizedValue) return null;
        return `${prettyLabel(key)}: ${normalizedValue}`;
      })
      .filter(Boolean);
  };

  const parts = partsFromObject(extraRoot);
  const vendors = Array.isArray(service.vendors) ? service.vendors : [];
  for (const v of vendors) {
    let ve = v.extra_fields;
    if (ve == null) ve = {};
    if (typeof ve === "string") {
      try {
        ve = ve.trim() ? JSON.parse(ve) : {};
      } catch {
        ve = {};
      }
    }
    if (typeof ve !== "object" || Array.isArray(ve)) ve = {};
    const perVendor = partsFromObject(ve);
    const vLabel = (v.vendor_name || "Vendor").trim();
    if (perVendor.length) {
      parts.push(`[${vLabel}] ${perVendor.join("; ")}`);
    }
  }

  const preamble = [];
  if (service.service_type) {
    preamble.push(
      `Type: ${String(service.service_type).replace(/_/g, " ")}`,
    );
  }

  if (Array.isArray(service.passenger_assignments) && service.passenger_assignments.length) {
    const names = service.passenger_assignments
      .map((p) =>
        (p.full_name || p.name || "").trim() || (p.passenger_id != null ? String(p.passenger_id) : ""),
      )
      .filter(Boolean);
    if (names.length) {
      preamble.push(`Passengers: ${names.join(", ")}`);
    }
  }

  if (Array.isArray(service.service_entities) && service.service_entities.length) {
    const labels = service.service_entities
      .map((ent) => formatServiceEntityLabel(ent))
      .filter(Boolean);
    if (labels.length) {
      preamble.push(`Linked: ${labels.join(", ")}`);
    }
  }

  if (longDescription && longDescription !== serviceName.trim()) {
    preamble.push(longDescription);
  }
  if (service.notes && String(service.notes).trim()) {
    preamble.push(String(service.notes).trim());
  }

  const detail = parts.length ? parts.join(" | ") : "";
  if (preamble.length && detail) {
    return `${serviceName} — ${preamble.join(" | ")} — ${detail}`;
  }
  if (preamble.length) {
    return `${serviceName} — ${preamble.join(" | ")}`;
  }
  if (detail) {
    return `${serviceName} — ${detail}`;
  }
  return serviceName;
}
